// ============================================================================
// EXTEND INTEGRATION — Checkout SP + Checkout+ only
// ============================================================================

const _scriptEl = document.currentScript;

// ============================================================================
// LOADERS
// ============================================================================

function loadScript(src) {
    return new Promise((resolve, reject) => {
        const script   = document.createElement('script');
        script.src     = src;
        script.onload  = resolve;
        script.onerror = () => reject(new Error(`Extend: failed to load script: ${src}`));
        document.head.appendChild(script);
    });
}


async function loadExtendSDKs() {
    await loadScript('https://sdk.helloextend.com/extend-sdk-client/v1/extend-sdk-client.min.js');
    await loadScript('https://sdk.helloextend.com/extend-sdk-client-shopify-addon/v1/extend-sdk-client-shopify-addon.min.js');
}

// ============================================================================
// GLOBALS & CONFIGURATION
// ============================================================================
const storeId    = 'b1eb7bfa-24c5-44c2-ba10-ff262ff38c7e';
const env        = 'production';
const isMainCart = location.pathname.includes('/cart');

let cart = null;

const config = {
    isMainCart,
    defaultCheckoutButton: null,
    checkoutPlusButton:    null,
    spLocation:            null,
    spInsertType:          null,
    quoteState:            null,
};

const state = {
    SPOffer:            {},
    originalButton:     null,
    checkoutPlusButton: null,
    buttonsInitialized: false,
};

// ============================================================================
// UTILITIES
// ============================================================================

const mkEl = (tag, cls) => {
    const el = document.createElement(tag);
    if (cls) el.className = cls;
    return el;
};

const formatCentsToDollars = (cents) => `$${(cents / 100).toFixed(2)}`;
const cartType = () => (config.isMainCart ? 'main' : 'side');

function isValidCartContext() {
    if (config.isMainCart) return true;
    return !!document.querySelector('.fa-shopping-cart');
}

async function fetchCart() {
    const res = await fetch('/cart.js');
    cart = await res.json();
    return cart;
}

// Shadow-root aware querySelector — tries normal DOM first, then walks shadow roots
function queryEl(selector) {
    const el = document.querySelector(selector);
    if (el) return el;
    for (const host of document.querySelectorAll('*')) {
        if (host.shadowRoot) {
            const found = host.shadowRoot.querySelector(selector);
            if (found) return found;
        }
    }
    return null;
}

// Shadow-root aware querySelectorAll — tries normal DOM first, then walks shadow roots
function queryAllEl(selector) {
    const els = [...document.querySelectorAll(selector)];
    if (els.length) return els;

    const results = [];
    for (const host of document.querySelectorAll('*')) {
        if (host.shadowRoot) {
            results.push(...host.shadowRoot.querySelectorAll(selector));
        }
    }
    return results;
}

// Inject styling into shadow-root of host element
function injectShadowStyles(host, css) {
    if (!host.shadowRoot) return;
    const style = document.createElement('style');
    style.textContent = css;
    host.shadowRoot.appendChild(style);
}

// ============================================================================
// INITIALIZATION
// ============================================================================

function initExtendConfiguration() {
    if (!window.Extend || !window.ExtendShopify) return;

    Extend.config({ storeId, environment: env });

    Extend.integration = {
        currency:     'USD',
        checkoutPlus: true,
    };

    if (Shopify?.currency?.active !== Extend.integration.currency) return;

    fetchCart()
        .then(() => initCheckoutPlusOffer(cart))
        .catch(err => console.error('Extend: could not fetch cart:', err));
}

// ============================================================================
// BOOT
// ============================================================================

loadExtendSDKs()
    .then(initExtendConfiguration)
    .catch(err => console.error('Extend: SDK failed to load:', err));

// ============================================================================
// CHECKOUT PLUS — CONFIGURATION
// ============================================================================

function setupCheckoutPlusConfiguration() {
    if (config.isMainCart) {
        config.defaultCheckoutButton = 'div[cart-checkout-container] a';
        config.checkoutPlusButton    = '.extend-checkout-plus-button';
        config.spLocation            = 'div[cart-checkout-container]';
        config.spInsertType          = 'before';
    } else {
        config.defaultCheckoutButton = '.upcart-checkout-button-container a';
        config.checkoutPlusButton    = '.extend-checkout-plus-button-side';
        config.spLocation            = '.upcart-footer';
        config.spInsertType          = 'before';
    }
}

function initCheckoutPlusOffer(initialCart) {
    cart = initialCart;
    setupCheckoutPlusConfiguration();
    initCartUpdateListeners();
    window.dispatchEvent(new Event('extendSPHelperLoaded'));
    renderOrUpdate(cart);
}

function initCartUpdateListeners() {
    const cartEvent = isMainCart ? 'refreshAjaxCart' : 'refreshAjaxSideCart';

    if (!isMainCart) return;
    document.addEventListener('cart:updateCart', (e) => {
        fetchCart()
            .then(updatedCart => renderOrUpdate(updatedCart))
            .catch(err => console.error('Extend:', err));
    });

    window.addEventListener(cartEvent, () => {
        fetchCart()
            .then(updatedCart => renderOrUpdate(updatedCart))
            .catch(err => console.error('Extend:', err));
    });

    window.addEventListener('refreshSP', () => location.reload());
}

// ============================================================================
// CHECKOUT PLUS — SP OFFER RENDERING
// ============================================================================

function dispatchSpQuoteUpdate(source, quote, extra = {}) {
    window.dispatchEvent(new CustomEvent('quoteUpdate', {
        detail: { source, quote, ...extra },
    }));
}

function renderOrUpdate(spcart) {
    if (!isValidCartContext()) return;

    const type        = cartType();
    const mappedItems = ExtendShopify.spCartMapper(spcart.items);

    if (Extend.shippingProtection?._instance && state.SPOffer[type]) {
        if (!queryEl(`#extend-shipping-offer-${type}`)) insertOffer();
        Extend.shippingProtection.update({ items: mappedItems });
    } else {
        state.SPOffer[type]    = mkEl('div');
        state.SPOffer[type].id = `extend-shipping-offer-${type}`;
        state.SPOffer[type].style.cssText = `
            position: absolute !important;
            width: 1px !important;
            height: 1px !important;
            margin: -1px !important;
            padding: 0 !important;
            overflow: hidden !important;
            clip: rect(0, 0, 0, 0) !important;
            white-space: nowrap !important;
            border: 0 !important;
            opacity: 0 !important;
            pointer-events: none !important;
        `;
        insertOffer();

        Extend.shippingProtection.render({
            selector:  `#extend-shipping-offer-${type}`,
            items:     mappedItems,
            isShippingProtectionInCart: ExtendShopify.shippingProtectionInCart(spcart.items),
            convertQuoteToFormattedDisplayPrice: async (quote) => {
                dispatchSpQuoteUpdate('render/format', quote);
                return ExtendShopify.convertQuoteToFormattedDisplayPrice(quote);
            },
            onEnable:  (quote)            => dispatchSpQuoteUpdate('enable',  quote),
            onDisable: (quote)            => dispatchSpQuoteUpdate('disable', quote),
            onUpdate:  (quote, spActions) => dispatchSpQuoteUpdate('update',  quote, { spActions }),
        });

        addLearnMoreClickListener();
    }

    if (!state.buttonsInitialized) {
        initializeButtons();
        state.buttonsInitialized = true;
    }

    const upcartHost = document.querySelector('#upCart'); // whatever the custom element tag is
    injectShadowStyles(upcartHost, `
        #CartPopup {
            z-index: 9999;
        }

        .upcart-backdrop {
            z-index: 9998;
        }
    `);
}

function insertOffer() {
    const type    = cartType();
    const offerEl = state.SPOffer[type];
    if (!offerEl) return;

    if (Extend.shippingProtection?._instance && !offerEl.querySelector('.extend-shipping-offer')) {
        offerEl.append(Extend.shippingProtection._instance.el);
    }
    document.body.append(offerEl);
}

function normalizeCart(type = 'main') {
    ExtendShopify.updateExtendLineItems({
        balanceCart: true,
        callback: (err, data) => {
            if (!err && (data?.updates || data?.additions)) {
                window.dispatchEvent(new Event(type === 'main' ? 'updateExtendSP' : 'updateExtendSPSide'));
            }
            renderOrUpdate(data.cart);
        },
    });
}

let debounceTimer;

function initSideCartUpdateListeners() {
    if (typeof upcartSubscribeCartOpened === 'undefined') {
        setTimeout(initSideCartUpdateListeners, 100);
        return;
    }

    function onCartEvent() {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
            fetchCart()
                .then(cart => renderOrUpdate(cart))
                .catch(err => console.error('Extend:', err));
        }, 100);
    }

    upcartSubscribeCartOpened(onCartEvent);
    upcartSubscribeCartUpdated(onCartEvent);
}
if (!isMainCart) initSideCartUpdateListeners();

// ============================================================================
// CHECKOUT PLUS — BUTTONS
// ============================================================================

function initializeButtons(attempts = 0) {
    if (!isValidCartContext()) return;

    const merchantBtn = queryEl(config.defaultCheckoutButton);

    if (!merchantBtn) {
        if (attempts < 100) {
            setTimeout(() => initializeButtons(attempts + 1), 100);
        } else {
            console.error('Extend: could not find default checkout button after 10s');
        }
        return;
    }
    state.originalButton = merchantBtn.cloneNode(true);

    merchantBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        updateSPoptin(false);
    });

    const checkoutPlusBtn = mkEl('button');
    const checkoutPlusCls = config.checkoutPlusButton.slice(1);
    const themeCls        = config.isMainCart
        ? 'cart__checkout-button button'
        : 'btn btn--primary btn--style-two btn--full mini-cart__checkout';
    checkoutPlusBtn.className     = `${themeCls} ${checkoutPlusCls}`;
    checkoutPlusBtn.innerText     = 'Checkout+';
    checkoutPlusBtn.style.display = 'none';

    checkoutPlusBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        updateSPoptin(true);
    });

    merchantBtn.parentElement.prepend(checkoutPlusBtn);
    state.checkoutPlusButton = checkoutPlusBtn;

    queryEl('.cart__ctas')?.style.setProperty('flex-direction', 'column', 'important');

    window.addEventListener('quoteUpdate', handleQuoteUpdate);
}

function handleQuoteUpdate(event) {
    if (!isValidCartContext()) return;

    const { premium } = event.detail.quote;
    config.quoteState = premium;

    const merchantBtn     = queryEl(config.defaultCheckoutButton);
    const checkoutPlusBtn = state.checkoutPlusButton;
    if (!merchantBtn || !checkoutPlusBtn) return;

    premium === 0
        ? hideCheckoutPlus(merchantBtn, checkoutPlusBtn)
        : showCheckoutPlus(merchantBtn, checkoutPlusBtn, premium);
}
 
function hideCheckoutPlus(merchantBtn, checkoutPlusBtn) {
    checkoutPlusBtn.style.setProperty('display', 'none', 'important');
    if (state.originalButton) merchantBtn.replaceWith(state.originalButton.cloneNode(true));
}

function showCheckoutPlus(merchantBtn, checkoutPlusBtn, premium) {
    const formatted = formatCentsToDollars(premium);
 
    checkoutPlusBtn.textContent = 'Continue With Checkout+';
    checkoutPlusBtn.style.setProperty('display', 'block', 'important');
    checkoutPlusBtn.style.setProperty('background-color', '#0c233f', 'important');
    checkoutPlusBtn.style.setProperty('color', 'white', 'important');
    checkoutPlusBtn.style.setProperty('text-transform', 'uppercase', 'important');
    checkoutPlusBtn.style.setProperty('width', '100%', 'important');
    checkoutPlusBtn.style.setProperty('height', '48px', 'important');
    isMainCart ? checkoutPlusBtn.style.setProperty('font-size', '18px', 'important') : checkoutPlusBtn.style.setProperty('font-size', '14px', 'important');
    checkoutPlusBtn.style.setProperty('cursor', 'pointer', 'important');
    if (!isMainCart) checkoutPlusBtn.style.setProperty('font-weight', '600', 'important');

    if (merchantBtn.nodeName.toLowerCase() === 'input') {
        merchantBtn.value = 'Continue without checkout+';
    } else {
        merchantBtn.innerText = 'Continue without checkout+';
    }
    merchantBtn.style.setProperty('background', 'none',  'important');
    merchantBtn.style.setProperty('border',     'none',  'important');
    merchantBtn.style.setProperty('color',      '#0c233f', 'important');
    isMainCart ? merchantBtn.style.setProperty('font-size', '16px',  'important') : merchantBtn.style.setProperty('font-size', '13px',  'important');

    if (!queryEl(config.checkoutPlusButton)) {
        merchantBtn.parentElement.prepend(checkoutPlusBtn);
    }

    addLearnMoreClickListener();
    checkoutPlusBtn.before(createCheckoutPlusOffer(formatted));
}

async function updateSPoptin(enabled) {
    try {
        await fetchCart();
        await fetch('/cart/update.js', {
            method:  'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
                attributes: {
                    ...cart.attributes,
                    'Extend Shipping Protection': enabled ? 'selected' : 'declined',
                },
            }),
        });
        window.location.href = '/checkout';
    } catch (err) {
        console.error('Extend: error updating SP opt-in:', err);
    }
}

// ============================================================================
// CHECKOUT PLUS — OFFER UI
// ============================================================================

function addLearnMoreClickListener(attempts = 0) {
    const iframe = queryEl(`#extend-shipping-offer-${cartType()} div iframe`);

    if (!iframe && attempts < 50) {
        return setTimeout(() => addLearnMoreClickListener(attempts + 1), 100);
    }
 
    setTimeout(() => {
        try {
            const learnMoreBtn = iframe?.contentDocument?.querySelector('body .shipping-info-button');
            const infoIcon     = queryEl('.checkout-plus-info-icon');
            if (infoIcon && learnMoreBtn) {
                infoIcon.style.display = 'inline-block';
                infoIcon.addEventListener('click', () => learnMoreBtn.click());
            }
        } catch (err) {
            console.warn('Extend: could not access SP iframe content:', err);
        }
    }, 500);
}

function createCheckoutPlusOffer(formatted) {
    queryAllEl('.checkout-plus-container').forEach(el => el.remove());
    const container = mkEl('div', 'checkout-plus-container');
    container.style.setProperty('display', 'flex', 'important');
    container.style.setProperty('align-items', 'center', 'important');
    container.style.setProperty('justify-content', 'space-between', 'important');
    container.style.setProperty('width', isMainCart ? '440px' : '100%', 'important');
    container.style.setProperty('margin-bottom', '10px', 'important');
    container.appendChild(buildOfferImageSection());
    container.appendChild(buildOfferInfoSection());
    const price = mkEl('span', 'checkout-plus-price');
    price.textContent = formatted;
    container.appendChild(price);
    return container;
}
 
function buildOfferImageSection() {
    const wrap = mkEl('div', 'checkout-plus-image');
    wrap.style.setProperty('width', '45px', 'important');
    const row  = mkEl('div', 'checkout-plus-icon-row');
    const icon = mkEl('span', 'checkout-plus-package-icon');

    icon.innerHTML = `
        <svg viewBox="0 0 32 25" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8.86368 2.89709C6.3014 4.04539 3.32139 5.37654 2.2414 5.85423C0.866067 6.45876 0.239466 6.76369 0.142615 6.86533L0 7.01261V13.4701C0 18.998 0.0101108 19.943 0.0713075 20.0648C0.13756 20.1869 0.99325 20.5882 6.79045 23.1844C10.4428 24.8255 13.4942 26.167 13.5705 26.167C13.6519 26.167 13.9933 26.0298 14.3347 25.8671C23.56 21.3604 25.8625 20.2121 25.9541 20.0953C26.056 19.9632 26.056 19.8719 26.0711 13.5259C26.0813 7.4696 26.0762 7.07843 25.9948 6.94123C25.9184 6.82446 24.8129 6.26052 19.8666 3.82672C16.5455 2.19595 13.7642 0.84437 13.6775 0.834021C13.5604 0.818893 12.506 1.2711 8.86368 2.89709ZM18.9345 4.57881C21.8433 6.00656 24.2323 7.18511 24.2427 7.19546C24.2733 7.22598 19.6173 9.50215 19.5308 9.50215C19.4544 9.50215 8.5734 4.35536 8.51247 4.28928C8.4973 4.26884 8.50741 4.24867 8.53296 4.23328C8.56355 4.2232 9.7098 3.71499 11.0801 3.10037C12.4554 2.48548 13.5915 1.98259 13.6118 1.98259C13.6317 1.98232 16.0309 3.15106 18.9345 4.57881ZM12.4397 7.37327C15.333 8.75006 17.8293 9.93392 17.9921 10.0101L18.2773 10.1473L15.9189 11.3006L13.5655 12.459L7.74804 9.85245C4.5541 8.41462 1.93568 7.23075 1.93568 7.21032C1.93568 7.17476 7.02459 4.88851 7.11638 4.88346C7.15204 4.87842 9.54616 6.00125 12.4397 7.37327ZM7.03497 10.7417L12.9644 13.399L12.9796 19.0742C12.9846 22.1937 12.9745 24.7443 12.954 24.7443C12.9285 24.7443 10.2491 23.5503 6.99399 22.0921L1.06988 19.4348V13.7546C1.06988 10.6351 1.07493 8.07944 1.08505 8.07944C1.09516 8.07944 3.76945 9.27843 7.03497 10.7417ZM24.9504 13.8003L24.9353 19.4704L19.5457 22.1075C16.5758 23.5554 14.141 24.7446 14.1309 24.7446C14.1208 24.7446 14.1154 22.194 14.1208 19.0744L14.1359 13.4043L19.5101 10.7723C22.4645 9.32434 24.8996 8.13544 24.9249 8.13544C24.9454 8.13013 24.9558 10.6807 24.9504 13.8003Z" fill="black"/>
            <path d="M2.59274 10.6663C2.47567 10.7881 2.44507 10.8595 2.44507 11.0373C2.44507 11.1645 2.48578 11.3017 2.53686 11.3677C2.66937 11.5405 5.63395 12.9223 5.86836 12.9276C6.0924 12.9327 6.34197 12.7244 6.38295 12.511C6.41355 12.3483 6.3167 12.0994 6.18419 11.9877C6.12805 11.9471 5.42003 11.5965 4.6101 11.2154C2.95939 10.4376 2.85243 10.4121 2.59274 10.6663Z" fill="black"/>
            <path d="M2.7263 12.712C2.43096 12.8694 2.34422 13.2964 2.5632 13.535C2.70076 13.6926 5.66029 15.0898 5.85399 15.0898C6.06286 15.0898 6.30738 14.912 6.3582 14.7239C6.40902 14.5256 6.35314 14.2921 6.23075 14.17C6.09346 14.0381 3.09801 12.651 2.95539 12.651C2.88409 12.651 2.78218 12.6815 2.7263 12.712Z" fill="black"/>
            <path d="M27.4631 13.5371C29.2256 14.6578 30.718 15.1198 31.9172 15.0098L32.6096 14.9463L32.6438 15.6406C32.7711 18.2224 32.3627 20.3419 31.4495 21.9971C30.53 23.6634 29.1258 24.8096 27.3606 25.4766L27.1282 25.5645L26.8958 25.4785C25.1855 24.8523 23.8006 23.7589 22.8743 22.0977C21.956 20.4506 21.5152 18.2952 21.5862 15.5811L21.6038 14.8965L22.2874 14.9326C23.8073 15.0124 25.267 14.6873 26.6956 13.5742L27.0667 13.2852L27.4631 13.5371Z" fill="black" stroke="white" stroke-width="1.33333"/>
        </svg>
    `;

    row.appendChild(icon);
    wrap.appendChild(row);
    return wrap;
}

function buildOfferInfoSection() {
    const container    = mkEl('div', 'checkout-plus-info-container');
    container.style.textAlign = 'start';
    const topRow       = mkEl('div', 'checkout-plus-top-row');
    const titleWrapper = mkEl('div', 'checkout-plus-title-icon-container');
    const title        = mkEl('span', 'checkout-plus-title');
    title.style.fontWeight = '500';
    title.style.fontSize   = '18px';
    const subtext      = mkEl('span', 'checkout-plus-subtext');
    subtext.style.setProperty('width', '100%', 'important');
    subtext.style.fontSize = '14px';

    title.innerHTML = `
        Extend Checkout+
        <svg class="checkout-plus-info-icon" style="width:17px!important;cursor:pointer!important;display:inline-block!important;" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7 0.318359C10.6903 0.318359 13.6816 3.30973 13.6816 7C13.6816 10.6903 10.6903 13.6816 7 13.6816C3.30973 13.6816 0.318359 10.6903 0.318359 7C0.318359 3.30973 3.30973 0.318359 7 0.318359Z" stroke="black" stroke-width="0.636364"/>
            <path d="M7.58743 4.35647H6.41261V3.18164H7.58743V4.35647ZM7.58743 9.64318V5.53129H5.8252V6.70612H6.41261V9.64318H5.8252V10.818H8.17485V9.64318H7.58743Z" fill="black"/>
        </svg>
    `;

    subtext.textContent = 'Added protection against loss, theft, or damage';

    titleWrapper.appendChild(title);
    topRow.appendChild(titleWrapper);
    container.appendChild(topRow);
    container.appendChild(subtext);
    return container;
}

async function removeExtendShippingProtection() {
  // Fetch the current cart
  const response = await fetch('/cart.js');
  const cart = await response.json();

  // Find the line item matching the product title
  const item = cart.items.find(
    (i) => i.product_title === 'Extend Shipping Protection Plan'
  );

  if (!item) return; // Not in cart, nothing to do

  // Remove it by setting its quantity to 0 using the line item key
  await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: item.key, quantity: 0 }),
  });

  location.reload();
}

removeExtendShippingProtection();